import { Box, useMediaQuery, useTheme } from '@mui/material';
import { ChatClient } from '../../components/chat-system/ChatClient';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { useChatClient } from '../../components/chat-system/useChatClient';

const getViewport = () => {
  if (typeof window === 'undefined') return { height: 0, offsetTop: 0 };
  const vv = window.visualViewport;
  if (vv) return { height: vv.height, offsetTop: vv.offsetTop };
  return { height: window.innerHeight, offsetTop: 0 };
};

const Chat = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();
  const { conversations, setSelectedConversation } = useChatClient();
  const conversationId = searchParams.get('conversation');
  const [viewport, setViewport] = useState(getViewport);

  // Track the visual viewport — resize/scroll fire when the iOS keyboard appears
  const updateViewport = useCallback(() => {
    setViewport(getViewport());
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', updateViewport);
      vv.addEventListener('scroll', updateViewport);
    }
    window.addEventListener('resize', updateViewport);
    const orientationHandler = () => setTimeout(updateViewport, 100);
    window.addEventListener('orientationchange', orientationHandler);

    return () => {
      if (vv) {
        vv.removeEventListener('resize', updateViewport);
        vv.removeEventListener('scroll', updateViewport);
      }
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', orientationHandler);
    };
  }, [updateViewport]);

  // When the iOS keyboard opens it shrinks the *visual* viewport but not the layout
  // viewport, so Safari lets you pan the page under the keyboard — dragging the fixed
  // Topbar/search bar offscreen. The document root and <main> are already overflow:hidden,
  // so this isn't DOM scroll and CSS can't stop it; it's the browser's visual-viewport
  // pan. The only reliable fix is to swallow touchmove at the document level, while still
  // allowing gestures that land inside a genuinely scrollable element (the chat message
  // list, dialogs). The chat overlay is sized to the visual viewport, so nothing else
  // needs to scroll.
  useEffect(() => {
    if (!isMobile || typeof document === 'undefined') return;

    const isScrollable = (node) => {
      let el = node instanceof Element ? node : null;
      while (el && el !== document.body) {
        const { overflowY } = window.getComputedStyle(el);
        if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
          return true;
        }
        el = el.parentElement;
      }
      return false;
    };

    // Decide once per gesture (on touchstart) whether it started somewhere scrollable.
    let allowMove = false;
    const onTouchStart = (e) => {
      allowMove = e.touches.length === 1 && isScrollable(e.target);
    };
    const onTouchMove = (e) => {
      if (!allowMove && e.cancelable) e.preventDefault();
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
    };
  }, [isMobile]);

  // Keep the global Topbar (search bar) pinned to the visual viewport on mobile. When
  // the keyboard opens, iOS auto-scrolls the visual viewport up to reveal the focused
  // input (offsetTop > 0) — not a touch gesture, so the touchmove lock above can't stop
  // it. The chat overlay already compensates with offsetTop; mirror that on the Topbar
  // so it doesn't slide offscreen. The Topbar is a sibling of the chat overlay (not an
  // ancestor), so transforming it can't affect the overlay's position:fixed.
  useEffect(() => {
    if (!isMobile || typeof document === 'undefined') return;
    const topbar = document.querySelector('[data-app-topbar]');
    if (!topbar) return;
    const prevTransform = topbar.style.transform;
    topbar.style.transform = viewport.offsetTop
      ? `translateY(${viewport.offsetTop}px)`
      : prevTransform;
    return () => {
      topbar.style.transform = prevTransform;
    };
  }, [isMobile, viewport.offsetTop]);

  // Auto-select conversation from URL parameter
  useEffect(() => {
    if (conversationId && conversations.length > 0) {
      const conversation = conversations.find(c => c.id === parseInt(conversationId));
      if (conversation) {
        setSelectedConversation(conversation);
      }
    }
  }, [conversationId, conversations, setSelectedConversation]);

  // Mobile: anchor to visualViewport so iOS keyboard shrinks the chat instead of hiding the input
  if (isMobile) {
    const topbarHeight = 44;
    const mobileMenuHeight = 35;
    const chatHeight = Math.max(0, viewport.height - topbarHeight - mobileMenuHeight);
    // iOS scrolls the visual viewport up to reveal the focused input when the keyboard
    // opens, leaving visualViewport.offsetTop > 0. Since this container is position:fixed
    // (anchored to the layout viewport), we must add offsetTop to keep it pinned to the
    // visual viewport — otherwise the input jumps up far above the keyboard.
    const top = topbarHeight + viewport.offsetTop;

    return (
      <Box
        sx={{
          position: 'fixed',
          top,
          left: 0,
          right: 0,
          height: chatHeight,
          overflow: 'hidden',
          zIndex: 10,
        }}
      >
        <ChatClient />
      </Box>
    );
  }

  return (
    <Box className="md:pt-[43px] h-full w-full flex flex-col overflow-hidden">
      <ChatClient />
    </Box>
  );
};

export default Chat;
